import { useEffect, useState } from 'react';
import { v3Api } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

// Module-level memoization keyed by `${concessionaireId}|${propertyAccess}`.
// One trip per (user, scope) per page load. Filter chips that mount/unmount
// during edit mode don't refire the request.
const cache = new Map();

/**
 * Returns `{ properties: [{id,name,code}], loading, error }` for the user's
 * accessible property set. Used by PropertyChip to render labels.
 */
export function useProperties() {
    const { concessionaireId, propertyAccess } = useAuth();
    const cacheKey = `${concessionaireId || ''}|${propertyAccess || ''}`;
    const [state, setState] = useState(() => {
        const hit = cache.get(cacheKey);
        if (hit) return hit;
        return { properties: [], loading: !!concessionaireId, error: null };
    });

    useEffect(() => {
        if (!concessionaireId) return;
        const hit = cache.get(cacheKey);
        if (hit && !hit.loading) {
            setState(hit);
            return;
        }
        let cancelled = false;
        (async () => {
            setState(s => ({ ...s, loading: true, error: null }));
            try {
                const res = await v3Api.property.listByConcessionaire(concessionaireId, propertyAccess);
                const list = Array.isArray(res) ? res : (res?.data || []);
                const properties = list.map(p => ({
                    id: String(p._id || p.id || ''),
                    name: p.property_name || p.name || p.property_mall_code || 'Property',
                    code: p.property_mall_code || p.mall_code || '',
                }));
                const next = { properties, loading: false, error: null };
                cache.set(cacheKey, next);
                if (!cancelled) setState(next);
            } catch (err) {
                const next = { properties: [], loading: false, error: err.message || 'Failed to load properties' };
                if (!cancelled) setState(next);
            }
        })();
        return () => { cancelled = true; };
    }, [cacheKey, concessionaireId, propertyAccess]);

    return state;
}
